import React from "react"
import { AdFormProgressBannerHeader } from "./AdFormProgressBannerHeader"
import styles from "./AdFormNumberedStepper.module.css"
import {
    getAdFormSteps,
    AdFormStep,
} from "../../Steps/StepContainer/AdFormSteps"

const CHECKMARK_ICON = "/foodslaboEf/img/ProgressBanner/step-b/checkmark.svg"

const TOTAL_DOTS = 3
const DOT_LABELS = ["基本情報", "経歴", "設定・確認"]

type DotStatus = "pending" | "current" | "completed"

// sectionDotは0〜2（各セクション内）に加え、確認画面では3（=TOTAL_DOTS、全セクション完了）を取りうる
const getDotStatus = (dotIndex: number, sectionDot: number): DotStatus => {
    if (sectionDot > dotIndex) return "completed"
    if (sectionDot === dotIndex) return "current"
    return "pending"
}

// 指定したsectionDot値を持つステップの先頭・末尾indexを、配列から動的に求める
// （セクションごとのステップ数をハードコードせず、AD_FORM_STEPSの実配列を単一の情報源にするため）
const getSectionStepRange = (steps: AdFormStep[], sectionDotValue: number) => {
    const indices = steps.reduce<number[]>((acc, step, i) => {
        if (step.sectionDot === sectionDotValue) acc.push(i)
        return acc
    }, [])
    return { start: indices[0], end: indices[indices.length - 1] }
}

// connectorIndexが指す罫線（dotIndex `connectorIndex` と `connectorIndex + 1` の間）の塗り進捗（0〜100）
// セクション通過済みなら100、未到達なら0、現在のセクション内ならそのセクション内の何ステップ目かに応じて連続的に伸ばす
const getConnectorFillPercent = (
    steps: AdFormStep[],
    currentStep: number,
    sectionDot: number,
    connectorIndex: number
): number => {
    if (sectionDot > connectorIndex) return 100
    if (sectionDot < connectorIndex) return 0

    const { start, end } = getSectionStepRange(steps, connectorIndex)
    if (end <= start) return 100

    return ((currentStep - start) / (end - start)) * 100
}

// 完了済みセクション(sectionDot > connectorIndex)だけオレンジ、進行中のセクションは濃いグレーで塗る
// （Figma実データ: 現在地に接する側は#7a7775、完了区間は#ff8000）
const getConnectorFillColor = (
    sectionDot: number,
    connectorIndex: number
): string => (sectionDot > connectorIndex ? "#ff8000" : "#7a7775")

interface AdFormProgressBannerProps {
    currentStep: number
}

export const AdFormProgressBanner = ({
    currentStep,
}: AdFormProgressBannerProps) => {
    const steps = getAdFormSteps()
    const sectionDot = steps[currentStep]?.sectionDot ?? 0

    return (
        <div className={styles.stepperContainer}>
            <div className={styles.headerContainer}>
                <AdFormProgressBannerHeader />
                <div className={styles.progressCardWrap}>
                    <div className={styles.progressCard}>
                        <div className={styles.stepRow}>
                            {Array.from(
                                { length: TOTAL_DOTS },
                                (_, dotIndex) => {
                                    const status = getDotStatus(
                                        dotIndex,
                                        sectionDot
                                    )
                                    // 丸と丸の間の罫線は、左右それぞれのstepUnitが半分ずつ描画する
                                    // （circleをstepUnitの中央に置くレイアウトのため、罫線が1本の要素ではなく
                                    // 前半・後半の2要素に分かれている）。そのため罫線全体の進捗(0-100%)を
                                    // 前半/後半それぞれの受け持ち分（0-100%を2倍に伸ばした値）に変換して塗る。
                                    // そのまま同じ%を両方に適用すると、進捗が2回繰り返されて途切れて見える。
                                    const connectorFillStyle = (
                                        connectorIndex: number,
                                        half: "leading" | "trailing"
                                    ) => {
                                        const overallPercent =
                                            getConnectorFillPercent(
                                                steps,
                                                currentStep,
                                                sectionDot,
                                                connectorIndex
                                            )
                                        const localPercent =
                                            half === "trailing"
                                                ? Math.min(
                                                      overallPercent * 2,
                                                      100
                                                  )
                                                : Math.max(
                                                      overallPercent * 2 - 100,
                                                      0
                                                  )
                                        const fillColor = getConnectorFillColor(
                                            sectionDot,
                                            connectorIndex
                                        )
                                        return {
                                            width: `${localPercent}%`,
                                            backgroundColor: fillColor,
                                        }
                                    }

                                    return (
                                        <div
                                            key={dotIndex}
                                            className={styles.stepUnit}
                                        >
                                            <div className={styles.stepLine}>
                                                {dotIndex === 0 ? (
                                                    <div
                                                        className={
                                                            styles.connectorSpacer
                                                        }
                                                    />
                                                ) : (
                                                    <div
                                                        className={
                                                            styles.connector
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                styles.connectorFill
                                                            }
                                                            style={connectorFillStyle(
                                                                dotIndex - 1,
                                                                "leading"
                                                            )}
                                                        />
                                                    </div>
                                                )}
                                                <div
                                                    className={`${styles.circle} ${
                                                        status === "completed"
                                                            ? styles.circleCompleted
                                                            : status ===
                                                                "current"
                                                              ? styles.circleCurrent
                                                              : styles.circlePending
                                                    }`}
                                                >
                                                    {status === "completed" ? (
                                                        <img
                                                            src={CHECKMARK_ICON}
                                                            alt="完了"
                                                            className={
                                                                styles.checkmarkIcon
                                                            }
                                                        />
                                                    ) : (
                                                        <span
                                                            className={
                                                                styles.circleNumber
                                                            }
                                                        >
                                                            {dotIndex + 1}
                                                        </span>
                                                    )}
                                                </div>
                                                {dotIndex === TOTAL_DOTS - 1 ? (
                                                    <div
                                                        className={
                                                            styles.connectorSpacer
                                                        }
                                                    />
                                                ) : (
                                                    <div
                                                        className={
                                                            styles.connector
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                styles.connectorFill
                                                            }
                                                            style={connectorFillStyle(
                                                                dotIndex,
                                                                "trailing"
                                                            )}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            <span
                                                className={`${styles.label} ${
                                                    status === "current"
                                                        ? styles.labelCurrent
                                                        : ""
                                                }`}
                                            >
                                                {DOT_LABELS[dotIndex]}
                                            </span>
                                        </div>
                                    )
                                }
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
